import { memo } from "react";
import { KeyRoles, RoleStatus } from "../../../types/enums";
import { RowWrapper } from "./RowWrapper";
import { DataSourceBodyCell } from "./DataSourceBodyCell";
import { TextCell } from "./TextCell";
import { SeniorityBodyCell } from "./SeniorityBodyCell";
import { RoleBodyCell } from "./RoleBodyCell";
import { CheckboxBodyCell } from "./CheckboxBodyCell";
import {
  CampaignMember,
  MembersUpdateValues,
} from "../../../types/companyTypes";
import { DATA_TYPES } from "../../../constant";
import { MemberMenu } from "../../ui";
import { useAppSelector } from "../../../hooks";

type Status = "committee_status" | "cluster_status";

type Props = CampaignMember & {
  isEditMode: boolean;
  updatedValues: MembersUpdateValues;
  updateHandler: (values: MembersUpdateValues) => void;
  setCommitteeSelected: React.Dispatch<React.SetStateAction<number>>;
  setClusterSelected: React.Dispatch<React.SetStateAction<number>>;
  isActive?: RoleStatus;
};

const getIsSelected = (status: string, check: string, isEndCheck?: boolean) => {
  if (!status) return false;
  return isEndCheck ? status.endsWith(check) : status.startsWith(check);
};

const INCLUDED = "included";
const MANUALLY = "manually";
const NO_PERSONA_MATCH = "No Persona Match";

export const RowItem = memo((props: Props) => {
  const {
    id,
    isEditMode,
    data_source,
    full_name,
    seniority,
    job_title,
    cluster_status,
    committee_status,
    role_id,
    warning_tooltip,
    linkedin_updated_at,
    updatedValues,
    is_edited,
    updateHandler,
    setCommitteeSelected,
    setClusterSelected,
    is_hidden,
    status: isActive,
    committee,
    cluster,
    engagement,
    sdr_playbook_status,
    is_activated,
    persona_name,
  } = props;

  const { isEditor } = useAppSelector((state) => state.company.userRoles);
  const isExternalData = data_source !== "crm_data";

  const personaValue = persona_name || (role_id ? NO_PERSONA_MATCH : "--");

  const isSelected =
    getIsSelected(cluster_status, INCLUDED, true) ||
    getIsSelected(committee_status, INCLUDED, true);

  const isEdited =
    getIsSelected(cluster_status, MANUALLY) ||
    getIsSelected(committee_status, MANUALLY);

  const isCommitteSelected =
    updatedValues?.committee === undefined
      ? getIsSelected(committee_status, INCLUDED, true)
      : updatedValues.committee;

  const isClusterSelected =
    updatedValues?.cluster === undefined
      ? getIsSelected(cluster_status, INCLUDED, true)
      : updatedValues.cluster;

  const selectedHandler = (type: Status) => {
    if (type === "committee_status") {
      const initCommittee = getIsSelected(committee_status, INCLUDED, true);
      const initCluster = getIsSelected(cluster_status, INCLUDED, true);
      const isSame = initCommittee === !isCommitteSelected;

      const committee = isSame ? undefined : !isCommitteSelected;
      const cluster =
        isCommitteSelected && isClusterSelected
          ? !initCluster
            ? undefined
            : false
          : isClusterSelected;

      setCommitteeSelected((prev) =>
        !isCommitteSelected ? prev + 1 : prev - 1,
      );

      isClusterSelected &&
        isCommitteSelected &&
        setClusterSelected((prev) => prev - 1);

      updateHandler({
        member_id: id,
        committee,
        cluster,
      });
    } else {
      const initIsSelected = getIsSelected(cluster_status, INCLUDED, true);
      const isSame = initIsSelected === !isClusterSelected;

      setClusterSelected((prev) => (!isClusterSelected ? prev + 1 : prev - 1));

      updateHandler({
        member_id: id,
        cluster: isSame ? undefined : !isClusterSelected,
      });
    }
  };

  const changeRoleHandler = (role: KeyRoles) => {
    const isSame = role_id === role;
    updateHandler({
      member_id: id,
      role: isSame ? undefined : role,
    });
  };

  return (
    <RowWrapper isHighlighted={isExternalData} is_activated={is_activated}>
      <DataSourceBodyCell
        is_hidden={is_hidden}
        isHighlighted={isSelected}
        data_source={
          data_source === "crm_data" ? DATA_TYPES.user : DATA_TYPES.infuse
        }
        value={full_name}
        maxWidth={"14%"}
        minWidth={"120px"}
        warning_tooltip={warning_tooltip}
        linkedin_updated_at={linkedin_updated_at}
        is_activated={is_activated}
      />
      <TextCell
        is_hidden={is_hidden}
        isHighlighted={isSelected}
        value={job_title}
        maxWidth={"16%"}
        minWidth={"100px"}
      />
      <SeniorityBodyCell
        value={seniority}
        maxWidth={"14%"}
        minWidth={"128px"}
        isEdited={isEdited || is_edited}
      />
      <RoleBodyCell
        changeRoleHandler={changeRoleHandler}
        isEditMode={isEditMode}
        value={updatedValues?.role || role_id}
        maxWidth={"12%"}
        minWidth={"124px"}
        isActive={isActive}
      />
      <TextCell
        is_hidden={is_hidden}
        isHighlighted={isSelected}
        value={personaValue}
        maxWidth={"12%"}
        minWidth={"120px"}
      />

      <TextCell
        indicator={engagement.indicator}
        indicatorChange={engagement.change}
        indicatorChangeAt={engagement.changed_at}
        isHighlighted={isSelected}
        value={`${engagement.score === null ? 0 : engagement.score}%`}
        maxWidth={"12%"}
        minWidth={"162px"}
        justifyContent="flex-end"
      />
      <CheckboxBodyCell
        indicator={committee.indicator}
        indicatorChange={committee.change}
        indicatorChangeAt={committee.changed_at}
        isHighlighted={isSelected}
        isSelected={isCommitteSelected}
        isEditMode={isEditMode}
        value={`${committee.score === null ? 0 : committee.score}%`}
        maxWidth={"10%"}
        minWidth={"148px"}
        justifyContent="flex-end"
        onChangeHandler={() => selectedHandler("committee_status")}
      />
      <CheckboxBodyCell
        indicator={cluster.indicator}
        indicatorChange={cluster.change}
        indicatorChangeAt={cluster.changed_at}
        isHighlighted={isSelected}
        isSelected={isClusterSelected}
        isDisabled={!isCommitteSelected}
        isEditMode={isEditMode}
        value={`${cluster.score === null ? 0 : cluster.score}%`}
        maxWidth={"10%"}
        minWidth={"124px"}
        justifyContent="flex-end"
        onChangeHandler={() => selectedHandler("cluster_status")}
      />
      {isEditor && (
        <MemberMenu
          member_id={id}
          isDisabled={false}
          playbookStatus={sdr_playbook_status}
        />
      )}
    </RowWrapper>
  );
});
